import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import debounce from "debounce-async";
import { useOktaAuth } from "@okta/okta-react";
import { Button, TextField, Grid, Typography } from "@deere/fuel-react";
import { getDeereContacts } from "../../services/external.service";
import { ERR_EXISTING_CONTACT, ERR_SELECT_OPTION } from "../../utils/errorLiterals";

const AutocompleteDeereContacts = ({ deereContacts, setDeereContacts, valid, isNonDeere }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [newInput, setNewInput] = useState(null);
  const [error, setError] = useState("");
  const [lastOptions, setLastOptions] = useState([]);
  const [isAddDisabled, setIsAddDisabled] = useState(false);
  const { authState } = useOktaAuth();

  useEffect(() => {
    if (isNonDeere) {
      valid && newInput && newInput.value !== "" ? setIsAddDisabled(false) : setIsAddDisabled(true);
    } else {
      valid && lastOptions.length > 0 ? setIsAddDisabled(false) : setIsAddDisabled(true);
    }
  }, [valid, lastOptions, newInput]);

  const isOptionSelected = (option, values) => {
    return values.some(({ value }) => option.value === value);
  };

  const changeHandler = (event) => {
    isNonDeere ? setNewInput({ label: event.target.value, value: event.target.value }) : setNewInput(event);
    setError("");
  };

  const loadOptions = React.useMemo(() => {
    return debounce(async (inputValue) => {
      let options = undefined;
      const suppliers = await getDeereContacts(inputValue, authState.accessToken.accessToken);
      options = suppliers.map((item) => ({ label: item.userLastname + ", " + item.userFirstname + " - " + item.userid, value: item.userid }));
      setLastOptions(options);
      return options;
    }, 300);
  }, [authState.accessToken.accessToken]);

  const handleInputChange = React.useCallback((typedOption) => {
    typedOption.length > 1 ? setShowOptions(true) : setShowOptions(false);
  }, []);

  const customStyles = {
    option: (provided, state) => ({ ...provided, color: state.isSelected ? "black" : "black" }),
    control: (base) => ({ ...base, height: 35, minHeight: 35, width: "auto" }),
    menu: (base) => ({ ...base, position: "relative" }),
    dropdownIndicator: (base) => ({ ...base, height: 35, minHeight: 35 }),
    placeholder: (base) => ({ ...base }),
    valueContainer: (base) => ({ ...base, height: 35, minHeight: 35 }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      return { ...provided, opacity };
    },
  };

  const addHandler = () => {
    if (newInput && newInput !== null) {
      let alreadyExists = deereContacts.find((item) => item.value === newInput.value);
      if (alreadyExists) setError(ERR_EXISTING_CONTACT);
      else {
        setDeereContacts((prevState) => [...prevState, newInput]);
        setNewInput("");
        setError("");
      }
    } else setError(ERR_SELECT_OPTION);
  };

  return (
    <>
      <Grid container justifyContent="space-around" spacing={2}>
        <Grid item xs={10}>
          {isNonDeere ? (
            <TextField placeholder="Enter Company Contact" value={newInput ? newInput.value : ""} onChange={changeHandler} disabled={!valid} />
          ) : (
            <AsyncSelect
              cacheOptions={true}
              defaultOptions
              loadOptions={showOptions ? loadOptions : null}
              isOptionSelected={isOptionSelected}
              value={newInput}
              onInputChange={handleInputChange}
              isClearable={true}
              styles={customStyles}
              noOptionsMessage={() => "Start Typing..."}
              onChange={changeHandler}
              isDisabled={!valid}
            />
          )}
        </Grid>
        <Grid item xs={2}>
          <Button variant="secondary" disabled={isAddDisabled} onClick={addHandler}>
            Add
          </Button>
        </Grid>
      </Grid>
      {error !== "" && <Typography variant="div" style={{ color: "red", fontSize: "smaller" }}>{error}</Typography>}
    </>
  );
};

export default AutocompleteDeereContacts;
